/**
 * ノードコマンド（ARCHITECTURE.md §4.5 が唯一の正）。
 *
 * long-poll（`POST /api/v1/agent/commands/poll`）で払い出し、結果は
 * `POST /api/v1/agent/commands/:commandId/result` で返す。
 * 真実は Postgres の `node_commands` テーブルで、`FOR UPDATE SKIP LOCKED`
 * により多重配送を防ぐ。Redis pub/sub（`mailsv:node-cmd`）は long-poll を
 * 起こすためだけに使い、コマンド本体は載せない（取りこぼしを許さないため）。
 *
 * **`restart.service` は存在しない。** 特権ヘルパ（shim）に restart 動詞が
 * 無く実現不能なので、再起動は `update.apply` 経由に一本化する。
 */
import { z } from 'zod';
import { signedReleaseDescriptorSchema } from './release.js';

export const NODE_COMMAND_KIND = [
  'reload.postfix',
  'reload.dovecot',
  'reload.rspamd',
  /** Dovecot auth cache の全件フラッシュ。パスワード変更・凍結の即時反映用 */
  'auth.flush',
  /** 次の long-poll 周期を待たず即座に desired-state を pull し直す */
  'apply.state',
  'update.apply',
  'update.rollback',
  'cert.install',
  'mailbox.recalc-quota',
  /** `purge_after` を待たない運営者操作 */
  'mailbox.purge',
  /**
   * メールボックス実体（Maildir）を **mbox に固めて S3 へ出す**（R56 / R101 / A163）。
   *
   * 本文はノードにしか無い（§7 不変条件 9）ので、契約者へメールデータを
   * 返す経路はこれしかない。利用規約 第 8 条は「解約後 30 日間データを
   * 取得可能な状態で保持」と約束しているのに、取得する手段が無かった。
   *
   * **CP は書き込み先の署名付き URL（PUT・単一キー・期限つき）を渡す。**
   * ノードにバケット全体の資格情報を持たせない（ノードが侵害されたときに
   * 他契約者のエクスポートまで読まれる）ための設計。
   */
  'mailbox.export',
  'diag.collect',
  'ip.block',
  'ip.unblock',
  /**
   * バックアップからの復元（`restore_runs` の実行。0016 で enum に追加）。
   * **必ず staging へ出す。** live の Maildir は backup コンテナから ro なので
   * 物理的に上書きできない。差し替えは人間の判断に残す。
   */
  'backup.restore',
  /**
   * 一斉配信の緊急停止（0055 で DB enum にも追加）。
   *
   * **リース済みのバッチはノードのローカル DB にある。** CP 側で
   * `campaigns.status='halted'` と `campaign_batches.state='canceled'` を
   * 書いても、既に払い出した分はノードが「CP 断でも最後まで流す」設計どおり
   * 送り切ってしまう（1 ノードあたり最大 2,000 宛先、最長リース期限ぶん）。
   * 送信は取り消せないので、停止はノードへ即時に伝える必要がある。
   */
  'bulk.halt',
  'agent.selfupdate',
] as const;
export type NodeCommandKind = (typeof NODE_COMMAND_KIND)[number];
export const nodeCommandKindSchema = z.enum(NODE_COMMAND_KIND);

/* ------------------------------------------------------------ 引数の検証 */

/**
 * shim / netguard に渡る値は**シェルに触れさせない**前提だが、
 * それでも CP 側で厳格に検証してから配る（多層防御。§7.9）。
 */
const emailAddressSchema = z
  .string()
  .regex(/^[a-z0-9._+-]+@[a-z0-9.-]+$/, 'アドレスの形式が不正です')
  .max(320);

const domainSchema = z
  .string()
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'ドメイン名の形式が不正です')
  .max(253);

const localPartSchema = z
  .string()
  .regex(/^[a-z0-9._+-]+$/, 'ローカルパートの形式が不正です')
  .max(64)
  // `..` は shim 側でも弾くが、ここでも落として二重に塞ぐ。
  .refine((v) => !v.includes('..') && v !== '.' && v !== '..', 'ローカルパートに ".." は使えません');

/** IPv4/IPv6 の CIDR。`net.ParseIP` に渡す前の一次検証。 */
const cidrSchema = z
  .string()
  .regex(
    /^(\d{1,3}(\.\d{1,3}){3}\/\d{1,2}|[0-9a-fA-F:]+\/\d{1,3})$/,
    'CIDR の形式が不正です',
  )
  .max(64);

/**
 * restic のスナップショット指定。`latest` か 8〜64 桁の 16 進 ID だけ。
 * **`apps/agent/src/ctl/shim.ts` の `SNAPSHOT_RE` と同じ規則**にしてある
 * （片方だけ緩めると、通ったのにノードで弾かれる無言の失敗になる）。
 */
const resticSnapshotSchema = z
  .string()
  .regex(/^(latest|[0-9a-f]{8,64})$/, 'スナップショット指定が不正です')
  .max(64);

/** netguard の kernel timeout に渡す TTL（§7.9 の 60..604800 秒）。 */
export const BAN_TTL_MIN_SEC = 60;
export const BAN_TTL_MAX_SEC = 604800;

/* ------------------------------------------------------------ コマンド型 */

const empty = z.object({}).strict();

export const nodeCommandPayloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('reload.postfix'), args: empty.default({}) }),
  z.object({ kind: z.literal('reload.dovecot'), args: empty.default({}) }),
  z.object({ kind: z.literal('reload.rspamd'), args: empty.default({}) }),
  z.object({ kind: z.literal('auth.flush'), args: empty.default({}) }),
  z.object({ kind: z.literal('apply.state'), args: empty.default({}) }),

  z.object({
    kind: z.literal('update.apply'),
    // CP 署名済み blob をそのまま中継する。エージェントは中身を組み立てない。
    args: z.object({ releaseDescriptor: signedReleaseDescriptorSchema }),
  }),
  z.object({ kind: z.literal('update.rollback'), args: empty.default({}) }),

  z.object({
    kind: z.literal('cert.install'),
    args: z.object({ certId: z.string().min(1) }),
  }),

  z.object({
    kind: z.literal('mailbox.recalc-quota'),
    // 省略時は全件（`doveadm quota recalc -A` 相当）。
    args: z.object({ address: emailAddressSchema.optional() }).default({}),
  }),
  z.object({
    kind: z.literal('mailbox.purge'),
    args: z.object({ domain: domainSchema, localPart: localPartSchema }),
  }),
  z.object({
    kind: z.literal('mailbox.export'),
    args: z.object({
      domain: domainSchema,
      localPart: localPartSchema,
      /** `export_jobs.id`。結果の紐づけに使う。 */
      exportId: z.string().min(1).max(64),
      /** 置き先のオブジェクトキー（CP が決める。ノードには決めさせない）。 */
      objectKey: z.string().min(1).max(512),
      /**
       * 署名付き PUT の URL。**このキー 1 本にしか書けない**。
       * `https` 以外を弾くのは、平文でエクスポートを流す経路を作らないため
       * （dev の MinIO は http なので、そこは `uploadUrlInsecureAllowed` を
       * 立てた環境でのみ許す……のではなく、dev では別途 http を許す
       * 判断をエージェント側に置く。CP は常に設定どおりの URL を渡す）。
       */
      uploadUrl: z.string().url().max(4096),
      /** 現時点では mbox のみ。将来 `tar.gz`（Maildir そのまま）を足せる形にしておく。 */
      format: z.enum(['mbox']).default('mbox'),
    }),
  }),

  z.object({
    kind: z.literal('diag.collect'),
    args: z.object({
      includeLogs: z.boolean().default(false),
      sinceMinutes: z.number().int().positive().max(1440).default(60),
    }),
  }),

  z.object({
    kind: z.literal('ip.block'),
    args: z.object({
      cidr: cidrSchema,
      ttlSec: z.number().int().min(BAN_TTL_MIN_SEC).max(BAN_TTL_MAX_SEC),
      reason: z.string().min(1).max(200),
    }),
  }),
  z.object({
    kind: z.literal('ip.unblock'),
    args: z.object({ cidr: cidrSchema }),
  }),

  /*
   * バックアップからの復元（`restore_runs` の実行）。
   *
   * **必ず staging へ出す。live の Maildir は上書きしない。**
   * `backup.sh` 側も live を ro でマウントしていて物理的に書けないが、
   * 「戻す」と「差し替える」を分けるのは設計上の判断なので、
   * ここで scope を限定しておく。差し替えは人間が別途行う。
   *
   * `snapshot` は restic のスナップショット指定。`latest` か 16 進 ID だけを
   * 通す（shim 側も `SNAPSHOT_RE` で同じ検査をする。多層防御）。
   */
  z.object({
    kind: z.literal('backup.restore'),
    args: z.discriminatedUnion('scope', [
      z.object({
        scope: z.literal('mailbox'),
        domain: domainSchema,
        localPart: localPartSchema,
        snapshot: resticSnapshotSchema,
        restoreRunId: z.string().min(1).max(64),
      }),
      z.object({
        scope: z.literal('conf'),
        snapshot: resticSnapshotSchema,
        restoreRunId: z.string().min(1).max(64),
      }),
      z.object({
        scope: z.literal('db'),
        snapshot: resticSnapshotSchema,
        restoreRunId: z.string().min(1).max(64),
      }),
    ]),
  }),

  /*
   * 一斉配信の緊急停止。**リース済みのバッチを破棄させる。**
   *
   * `campaignId` が null なら全キャンペーン（運営の全社キルスイッチ）。
   * ノード側は未注入の宛先を `skipped` として CP へ返すので、
   * 「止めたのに何件残っていたか」が CP から見える。
   */
  z.object({
    kind: z.literal('bulk.halt'),
    args: z.object({
      campaignId: z.string().min(1).max(64).nullable().default(null),
      /** 語彙は固定（自由文を入れない）。画面と監査ログの両方で使う。 */
      reason: z.enum(['halted_by_org', 'halted_by_ops', 'kill_switch']).default('halted_by_ops'),
    }),
  }),

  z.object({
    kind: z.literal('agent.selfupdate'),
    args: z.object({ releaseDescriptor: signedReleaseDescriptorSchema }),
  }),
]);
export type NodeCommandPayload = z.infer<typeof nodeCommandPayloadSchema>;

/** 払い出し時にエージェントへ渡す封筒。 */
export const nodeCommandSchema = z.object({
  /** `node_commands.id`（ULID） */
  id: z.string().min(1),
  nodeId: z.string().min(1),
  issuedAt: z.string().datetime(),
  /** これを過ぎたら実行せず `expired` を返す。 */
  expiresAt: z.string().datetime(),
  /** 再配送回数。3 を超えると CP 側で `failed` に落とす。 */
  redeliveryCount: z.number().int().nonnegative().default(0),
  payload: nodeCommandPayloadSchema,
});
export type NodeCommand = z.infer<typeof nodeCommandSchema>;

/* -------------------------------------------------------------- 実行結果 */

export const NODE_COMMAND_RESULT_STATUS = ['succeeded', 'failed', 'rejected', 'expired'] as const;
export type NodeCommandResultStatus = (typeof NODE_COMMAND_RESULT_STATUS)[number];
export const nodeCommandResultStatusSchema = z.enum(NODE_COMMAND_RESULT_STATUS);

export const nodeCommandResultSchema = z.object({
  commandId: z.string().min(1),
  status: nodeCommandResultStatusSchema,
  /** 画面に出す短い説明。秘密情報を入れてはならない。 */
  message: z.string().max(2000).nullable().default(null),
  durationMs: z.number().int().nonnegative(),
  finishedAt: z.string().datetime(),
  /** `diag.collect` の成果物 URL など。構造は kind 依存。 */
  detail: z.record(z.string(), z.unknown()).default({}),
});
export type NodeCommandResult = z.infer<typeof nodeCommandResultSchema>;

/* ------------------------------------------------------------ 配送パラメタ */

/** 応答がないまま経過したら再配送する時間（§4.5）。 */
export const NODE_COMMAND_REDELIVERY_AFTER_SEC = 600;
/** これを超える再配送で `failed` に落とす。 */
export const NODE_COMMAND_MAX_REDELIVERY = 3;
/** long-poll を起こすための Redis pub/sub チャンネル。本体は載せない。 */
export const NODE_COMMAND_CHANNEL = 'mailsv:node-cmd';

/** 特権ヘルパを呼ぶ＝運営者の明示操作としてリアルタイム監査すべき kind。 */
export const AUDITED_NODE_COMMAND_KINDS: readonly NodeCommandKind[] = [
  'update.apply',
  'update.rollback',
  'agent.selfupdate',
  'mailbox.purge',
  'ip.block',
  'ip.unblock',
  'cert.install',
  // 復元は staging へ出すだけで破壊的ではないが、**誰がいつ顧客データを
  // 取り出したか**は残す必要がある（個人情報保護法の安全管理措置）。
  'backup.restore',
  // エクスポートも同じ（メール本文がノードの外へ出る唯一の経路）。
  'mailbox.export',
  // 緊急停止は「何通が止まり、何通が既に出ていたか」を後から追える必要がある
  // （特定電子メール法の苦情対応も、事故報告も、ここが起点になる）。
  'bulk.halt',
];
